import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import type { OrderAction } from '@nokowazzi/domain';
import { formatMoney } from '@nokowazzi/types';
import type { CurrencyCode } from '@nokowazzi/types';
import { Badge, Button, Dialog, EmptyState, EscrowStatusBar, SegmentedControl, Spinner, Textarea, toast } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { usePermissions } from '../features/admin/use-permissions';
import { adminOrderDetailQuery } from '../features/orders/queries';
import type { AdminOrderChatMessage, EscrowEventRow, PaymentRow } from '../features/orders/queries';
import { ORDER_STATUS_LABEL, OrderStatusBadge, toOrderStatus } from '../features/orders/status';
import { adminTransitionOptions, ORDER_ACTION_LABEL } from '../features/orders/transitions';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';

export const Route = createFileRoute('/_admin/orders/$id')({
  component: OrderDetailScreen,
});

const ACTOR_LABEL: Record<string, string> = {
  buyer: '구매자',
  seller: '판매자',
  admin: '관리자',
  system: '시스템',
};

const PAYMENT_KIND_LABEL: Record<string, string> = {
  charge: '결제',
  refund: '환불',
  payout: '정산 지급',
  partial_refund: '부분 환불',
};

function money(amount: number, currency: CurrencyCode): string {
  return formatMoney({ amount, currency });
}

function OrderDetailScreen() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const detailQuery = useQuery(adminOrderDetailQuery(id));
  const [tab, setTab] = useState<'timeline' | 'chat'>('timeline');
  const [pendingAction, setPendingAction] = useState<OrderAction | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const transitionMutation = useMutation({
    mutationFn: (input: { action: OrderAction; reason: string }) =>
      apiFetch<unknown>(`/v1/admin/orders/${id}/transition`, { method: 'POST', json: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      toast.show({ type: 'success', message: '상태를 변경했습니다' });
      closeDialog();
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '상태 변경에 실패했습니다',
      });
    },
  });

  const closeDialog = () => {
    setPendingAction(null);
    setReason('');
    setReasonError(null);
  };

  const submitTransition = () => {
    if (pendingAction === null) return;
    if (reason.trim() === '') {
      setReasonError('사유를 입력해 주세요');
      return;
    }
    transitionMutation.mutate({ action: pendingAction, reason: reason.trim() });
  };

  if (detailQuery.isPending) {
    return (
      <div className="flex min-h-52 items-center justify-center">
        <Spinner label="주문 불러오는 중" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <EmptyState
        title="주문을 불러오지 못했습니다"
        description={
          detailQuery.error instanceof ApiError ? detailQuery.error.message : '잠시 후 다시 시도해 주세요'
        }
        action={
          <Button variant="tertiary" size="sm" onClick={() => void detailQuery.refetch()}>
            다시 시도
          </Button>
        }
      />
    );
  }

  const { order, request, buyer, seller, shipment, payments, escrowEvents, dispute, chatMessages } =
    detailQuery.data;
  const status = toOrderStatus(order.status);
  const currency = order.currency;
  const transitionOptions = adminTransitionOptions(status);
  const canTransition = can('orders:transition');
  const canReadChat = can('chats:read');

  return (
    <>
      <div className="mb-4">
        <Link
          to="/orders"
          className="inline-flex items-center gap-1.5 text-caption1 text-fg-secondary hover:text-fg"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          거래 목록
        </Link>
      </div>

      <PageHeader
        title={`주문 ${order.id.slice(0, 8)}`}
        description={request?.title ?? undefined}
        actions={
          canTransition && transitionOptions.length > 0 ? (
            <Can perm="orders:transition">
              <div className="flex flex-wrap items-center justify-end gap-2">
                {transitionOptions.map((opt) => (
                  <Button
                    key={opt.value}
                    variant={opt.value === 'cancel' || opt.value === 'refund' ? 'danger' : 'secondary'}
                    size="sm"
                    onClick={() => {
                      setPendingAction(opt.value);
                      setReason('');
                      setReasonError(null);
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </Can>
          ) : undefined
        }
      />

      {/* 에스크로 스테퍼 */}
      <section className="mb-6 rounded-lg border border-line bg-surface p-6">
        <EscrowStatusBar status={status} labels={ORDER_STATUS_LABEL} />
      </section>

      <div className="grid grid-cols-3 gap-6">
        {/* 좌: 당사자/요청 요약 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">거래 정보</h2>
          <dl className="flex flex-col gap-2 text-caption1">
            <Row label="상태">
              <OrderStatusBadge status={order.status} />
            </Row>
            <Row label="구매자">{buyer?.display_name ?? '—'}</Row>
            <Row label="판매자">{seller?.display_name ?? '—'}</Row>
            <Row label="생성일">{formatDateTime(order.created_at)}</Row>
            {order.auto_confirm_at != null && (
              <Row label="자동확정 예정">{formatDateTime(order.auto_confirm_at)}</Row>
            )}
            {dispute != null && (
              <Row label="분쟁">
                <Link
                  to="/disputes/$id"
                  params={{ id: dispute.id }}
                  className="text-fg-brand hover:underline"
                >
                  분쟁 보기
                </Link>
              </Row>
            )}
          </dl>
        </section>

        {/* 중: 결제·정산 분해 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">금액 분해</h2>
          <dl className="flex flex-col gap-2 text-caption1">
            <Row label="상품금액">
              <span className="tabular-nums">{money(order.item_amount, currency)}</span>
            </Row>
            <Row label="배송비">
              <span className="tabular-nums">{money(order.shipping_amount, currency)}</span>
            </Row>
            <Row label={`수수료 (${Math.round(order.fee_rate * 100)}%)`}>
              <span className="tabular-nums">-{money(order.fee_amount, currency)}</span>
            </Row>
            <div className="mt-1 flex items-center justify-between border-t border-line pt-2">
              <dt className="font-medium text-fg">총 결제금액</dt>
              <dd className="tabular-nums font-semibold text-fg">{money(order.total_amount, currency)}</dd>
            </div>
            <div className="flex items-center justify-between text-fg-secondary">
              <dt>판매자 정산 예정</dt>
              <dd className="tabular-nums">
                {money(order.item_amount + order.shipping_amount - order.fee_amount, currency)}
              </dd>
            </div>
          </dl>
        </section>

        {/* 우: 배송 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">배송</h2>
          {shipment == null ? (
            <p className="text-caption1 text-fg-tertiary">배송 정보 없음</p>
          ) : (
            <dl className="flex flex-col gap-2 text-caption1">
              <Row label="택배사">{shipment.carrier ?? '—'}</Row>
              <Row label="송장번호">
                <span className="font-mono">{shipment.tracking_number ?? '—'}</span>
              </Row>
              <Row label="발송일">{formatDateTime(shipment.shipped_at) || '—'}</Row>
              <Row label="배송완료일">{formatDateTime(shipment.delivered_at) || '—'}</Row>
            </dl>
          )}
        </section>
      </div>

      {/* 결제 원장 */}
      <section className="mt-6">
        <h2 className="mb-3 text-title3 text-fg">결제 · 정산 원장</h2>
        {payments.length === 0 ? (
          <p className="rounded-lg border border-line bg-surface p-5 text-caption1 text-fg-tertiary">
            결제 기록이 없습니다
          </p>
        ) : (
          <PaymentsTable payments={payments} />
        )}
      </section>

      {/* 타임라인 / 채팅 탭 */}
      <section className="mt-6">
        <div className="mb-3 w-72">
          <SegmentedControl
            options={
              canReadChat
                ? [
                    { value: 'timeline', label: '이력 타임라인' },
                    { value: 'chat', label: '채팅 원문' },
                  ]
                : [{ value: 'timeline', label: '이력 타임라인' }]
            }
            value={tab}
            onChange={(value) => setTab(value === 'chat' ? 'chat' : 'timeline')}
          />
        </div>

        {tab === 'timeline' ? (
          <Timeline events={escrowEvents} />
        ) : (
          <ChatLog messages={chatMessages} />
        )}
      </section>

      {/* 수동 전이 확인 Dialog */}
      <Dialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        title={pendingAction != null ? (ORDER_ACTION_LABEL[pendingAction] ?? '상태 변경') : '상태 변경'}
        description="수동 상태 변경은 감사 로그에 기록됩니다. 사유를 반드시 입력하세요."
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={closeDialog}>
              취소
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={transitionMutation.isPending}
              onClick={submitTransition}
            >
              실행
            </Button>
          </>
        }
      >
        <Textarea
          label="사유 (필수)"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonError != null) setReasonError(null);
          }}
          maxLength={500}
          rows={3}
          error={reasonError ?? undefined}
          placeholder="변경 사유를 입력하세요"
        />
      </Dialog>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-fg-tertiary">{label}</dt>
      <dd className="text-right text-fg">{children}</dd>
    </div>
  );
}

function PaymentsTable({ payments }: { payments: PaymentRow[] }) {
  return (
    <Table>
      <Thead>
        <tr>
          <Th>종류</Th>
          <Th>프로바이더</Th>
          <Th className="text-right">금액</Th>
          <Th>상태</Th>
          <Th className="w-36">일시</Th>
        </tr>
      </Thead>
      <tbody>
        {payments.map((p) => (
          <Tr key={p.id}>
            <Td className="font-medium">{PAYMENT_KIND_LABEL[p.kind] ?? p.kind}</Td>
            <Td className="text-fg-secondary">{p.provider}</Td>
            <Td className="text-right tabular-nums">{money(p.amount, p.currency)}</Td>
            <Td>
              <Badge tone={p.status === 'succeeded' ? 'success' : p.status === 'failed' ? 'danger' : 'neutral'}>
                {p.status}
              </Badge>
            </Td>
            <Td className="whitespace-nowrap text-caption2 text-fg-tertiary">
              {formatDateTime(p.created_at)}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

function Timeline({ events }: { events: EscrowEventRow[] }) {
  if (events.length === 0) {
    return (
      <p className="rounded-lg border border-line bg-surface p-5 text-caption1 text-fg-tertiary">
        상태 변경 이력이 없습니다
      </p>
    );
  }
  return (
    <ol className="rounded-lg border border-line bg-surface p-5">
      {events.map((event, index) => (
        <li key={event.id} className="flex gap-3 pb-4 last:pb-0">
          <div className="flex flex-col items-center">
            <span className="mt-1 size-2.5 shrink-0 rounded-full bg-info-600" aria-hidden="true" />
            {index < events.length - 1 && <span className="w-px flex-1 bg-line" aria-hidden="true" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-caption1 font-medium text-fg">
              {event.from_status != null
                ? `${ORDER_STATUS_LABEL[toOrderStatus(event.from_status)]} → ${ORDER_STATUS_LABEL[toOrderStatus(event.to_status)]}`
                : ORDER_STATUS_LABEL[toOrderStatus(event.to_status)]}
            </p>
            <p className="mt-0.5 text-caption2 text-fg-tertiary">
              {ACTOR_LABEL[event.actor_type] ?? event.actor_type} · {formatDateTime(event.created_at)}
            </p>
            {event.reason != null && event.reason !== '' && (
              <p className="mt-1 text-caption2 text-fg-secondary">{event.reason}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function ChatLog({ messages }: { messages: AdminOrderChatMessage[] | undefined }) {
  if (messages === undefined) {
    return (
      <p className="rounded-lg border border-line bg-surface p-5 text-caption1 text-fg-tertiary">
        채팅 열람 권한이 없습니다
      </p>
    );
  }
  if (messages.length === 0) {
    return (
      <p className="rounded-lg border border-line bg-surface p-5 text-caption1 text-fg-tertiary">
        채팅 메시지가 없습니다
      </p>
    );
  }
  return (
    <div className="flex max-h-[32rem] flex-col gap-3 overflow-y-auto rounded-lg border border-line bg-surface p-5">
      {messages.map((m) => (
        <div key={m.id} className="flex flex-col gap-0.5">
          <div className="flex items-baseline gap-2">
            <span className="text-caption1 font-medium text-fg">{m.senderName ?? '시스템'}</span>
            <span className="text-caption2 text-fg-tertiary">{formatDateTime(m.createdAt)}</span>
            {m.type !== 'text' && <Badge tone="neutral">{m.type}</Badge>}
          </div>
          <p className="whitespace-pre-wrap break-words text-caption1 text-fg-secondary">
            {m.body ?? '(내용 없음)'}
          </p>
        </div>
      ))}
    </div>
  );
}
